import { useEffect } from 'react';
import { ArrowRightIcon, ClockIcon, ExternalLinkIcon, GripVerticalIcon, LayoutPanelTopIcon, MoveVerticalIcon, PhoneIcon, SquareIcon } from 'lucide-react';
import { business, images, interiorHeroes, routes } from '../data/site';
const hero = interiorHeroes.spaSteps;
import { SiteLink } from '../components/SiteLink';
import { Disclosure } from '../components/Disclosure';
const TITLE = 'Hot Tub Steps & Spa Access | Pegasus Spas';
const DESCRIPTION = 'Compare hot tub step and handrail options with Pegasus Spas in Brooklyn, Michigan, then confirm fit, footprint and availability with the showroom.';
const CANONICAL = 'https://www.pegasus-spas.com/46.html';
const checkItems = [{
  icon: MoveVerticalIcon,
  title: 'Spa Height & Entry Side',
  copy: 'Measure the cabinet height and note which side people actually use to get in and out.'
}, {
  icon: SquareIcon,
  title: 'Available Footprint',
  copy: 'Check how much floor space the steps can occupy without crowding the walkway.'
}, {
  icon: GripVerticalIcon,
  title: 'Handrail or Support Preference',
  copy: 'Decide whether anyone using the spa would prefer something to hold on to.'
}, {
  icon: LayoutPanelTopIcon,
  title: 'Surface & Placement',
  copy: 'Consider the surface underneath and whether it is level and stable where the steps sit.'
}];
const setups = [{
  title: 'Multi-level spa steps',
  copy: 'A staged stair-style layout that breaks the climb into a series of shorter rises beside the cabinet.',
  image: 'https://drwzpk38qkpfb.cloudfront.net/www.pegasus-spas.com/uploaded/images/New%20steps%202020/Ultra%20Spa%20step.jpg?opt=5',
  alt: 'Two-tier freestanding spa step unit'
}, {
  title: 'Steps with a handrail',
  copy: 'A step unit paired with an upright rail, for people who prefer something to hold while stepping up or down.',
  image: 'https://drwzpk38qkpfb.cloudfront.net/core/getthumb?file=uploaded%2Fimages%2FNew+steps+2020%2Fmodstep2XLrail.png&h=183&opt=5&site=10864&user=5980&w=156',
  alt: 'Spa steps with an upright handrail'
}, {
  title: 'Compact cabinet-side steps',
  copy: 'A smaller single-unit step that takes up less floor area where space beside the spa is tight.',
  image: 'https://drwzpk38qkpfb.cloudfront.net/www.pegasus-spas.com/uploaded/images/New%20steps%202020/SmartStep-Black.jpg315x195.jpg?opt=5',
  alt: 'Compact single-unit spa step'
}];
const manufacturers = [{
  label: 'Essentials Spa Supplies',
  href: 'https://www.essentials-spasupplies.com/'
}, {
  label: 'Sundance Spas',
  href: 'https://www.sundancespas.com/'
}, {
  label: 'A&B Accessories',
  href: 'https://www.aandbaccessories.com/'
}, {
  label: 'Leisure Concepts',
  href: 'https://www.leisureconcepts.com/'
}];
const relatedLinks = [{
  label: 'Explore Hot Tubs',
  href: routes.hotTubs
}, {
  label: 'Cover Lifts',
  href: routes.coverLifts
}, {
  label: 'Spa Service & Repair',
  href: routes.service
}, {
  label: 'Outdoor Furniture',
  href: routes.furniture
}];
function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
export function SpaStepsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = TITLE;
    upsertMeta('description', DESCRIPTION);
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);
  }, []);
  return <main data-id="element-2148" data-mp="xxl6dvh5ocao">
      {/* Intro band */}
      <section aria-labelledby="steps-h1" className="relative w-full" data-id="element-2149" data-mp="11wkl27sovnfb">
        <div className={`relative w-full overflow-hidden ${hero.frameClass}`} data-id="element-2150" data-mp="11yygwiv713nx">
          <img src={hero.src} alt={hero.alt} className={hero.imageClass} width={hero.width} height={hero.height} decoding="async" data-id="element-2151" data-mp="10g6rvbwqca9q" />
          <div className="hero-scrim-compact absolute inset-0 sm:hidden" aria-hidden="true" data-id="element-2152" data-mp="1abroeuj14ic3" />
          <div className="hero-scrim absolute inset-0 hidden sm:block" aria-hidden="true" data-id="element-2153" data-mp="15h4a1ohqp4b8" />

          <div className={`relative mx-auto flex max-w-container flex-col justify-center px-5 py-10 sm:px-8 ${hero.contentClass}`} data-id="element-2154" data-mp="1jcpudid12zxb">
            <p className="text-[14px] font-medium uppercase text-white sm:text-[15px]" data-id="element-2155" data-mp="1t4vlyqzdbfbz">
              Spa Accessories
            </p>
            <h1 id="steps-h1" className="mt-3 max-w-3xl text-h1-mobile font-semibold text-white lg:text-[48px] lg:leading-[56px]" data-id="element-2156" data-mp="1o6av3i60a807">
              Hot Tub Steps for a More Comfortable Entry
            </h1>
            <p className="mt-4 max-w-2xl text-body text-white sm:text-body-lg" data-id="element-2157" data-mp="2gs8zvomxduvs">
              Spa steps should fit the height of the cabinet, the room around the tub and the way
              people enter and exit. We can help you compare step and handrail options
              for a setup that works with the spa and its surroundings.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-2158" data-mp="2cokqd0eqgp7w">
              <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-2159" data-mp="1y0gf2hi0ffz5">
                Visit the Showroom
              </SiteLink>
              <SiteLink to={routes.hotTubs} className="inline-flex min-h-[48px] items-center rounded-btn border-2 border-white bg-white/10 px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-white hover:text-ink" data-id="element-2160" data-mp="1t2ocmtjqobgp">
                Explore Hot Tubs
              </SiteLink>
            </div>
          </div>
        </div>
      </section>

      {/* What to check */}
      <section aria-labelledby="check-heading" className="w-full bg-white" data-id="element-2161" data-mp="3cy2jur1c5ie6">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-2162" data-mp="3cw819jdje2d9">
          <h2 id="check-heading" className="max-w-3xl text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-2163" data-mp="381c1ty5z0cq4">
            What to Check Before Choosing Steps
          </h2>

          <ul className="mt-8 grid grid-cols-1 gap-x-10 gap-y-7 sm:grid-cols-2 lg:grid-cols-4" data-id="element-2164" data-mp="4gy0qfpap57l">
            {checkItems.map(item => {
            const Icon = item.icon;
            return <li key={item.title} data-id="element-2165" data-mp="3qsd0el36i695">
                  <Icon className="h-6 w-6 text-pine" aria-hidden="true" data-id="element-2166" data-mp="2ka6opqrezyji" />
                  <h3 className="mt-3 text-[19px] font-semibold leading-7 text-ink" data-id="element-2167" data-mp="2fb3jo6bfpsbj">
                    {item.title}
                  </h3>
                  <p className="mt-1.5 text-[16px] leading-6 text-ink/75" data-id="element-2168" data-mp="2587z54s3fy6n">{item.copy}</p>
                </li>;
          })}
          </ul>

          <p className="mt-7 max-w-3xl text-[16px] leading-6 text-ink/70" data-id="element-2169" data-mp="2f52z7s40m6n1">
            The selected product and its installation must follow the manufacturer's instructions.
          </p>
        </div>
      </section>

      {/* Step setups */}
      <section aria-labelledby="setups-heading" className="w-full border-y border-ink/10 bg-white" data-id="element-2170" data-mp="2nwd1q8x0ld3v">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-2171" data-mp="2sumvqg7067nw">
          <h2 id="setups-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-2172" data-mp="2xwvp3dnsfbqj">
            Compare Common Step Setups
          </h2>

          <ul className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-2173" data-mp="13ikyp0rjo9in">
            {setups.map(setup => <li key={setup.title} className="flex h-full flex-col rounded-card border border-ink/10 bg-white" data-id="element-2174" data-mp="18gwa3fxhms2z">
                <div className="aspect-[4/3] w-full bg-water" data-id="element-2175" data-mp="35fv7ph3fcz8u">
                  <img src={setup.image} alt={setup.alt} className="h-full w-full object-contain p-4" loading="lazy" decoding="async" data-id="element-2176" data-mp="3k3zt4stv3ovb" />
                </div>
                <div className="flex flex-1 flex-col p-5" data-id="element-2177" data-mp="2rfkdq56tj4jg">
                  <h3 className="text-h3 font-semibold text-ink" data-id="element-2178" data-mp="2mh0ptz8nziog">{setup.title}</h3>
                  <p className="mt-2 text-[16px] leading-6 text-ink/75" data-id="element-2179" data-mp="30gib44mr4dt2">{setup.copy}</p>
                  <p className="mt-auto pt-4 text-[14.5px] leading-5 text-ink/55" data-id="element-2180" data-mp="2wenbr5odsahx">
                    Ask us about fit and current options.
                  </p>
                </div>
              </li>)}
          </ul>
        </div>
      </section>

      {/* Whole spa area */}
      <section aria-labelledby="area-heading" className="w-full bg-water" data-id="element-2181" data-mp="j7p2933a54vt">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-2182" data-mp="j6e1lc54sd8x">
          <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-12 lg:gap-12" data-id="element-2183" data-mp="o7jvveefw8kk">
            <div className="lg:col-span-6" data-id="element-2184" data-mp="9at33nz9tv3x">
              <h2 id="area-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-2185" data-mp="9bvtlusolvjb">
                Match the Steps to the Whole Spa Area
              </h2>
              <p className="mt-4 text-body text-ink/80 sm:text-body-lg" data-id="element-2186" data-mp="16xt5h9ck32ma">
                Steps rarely sit in isolation. Think about the walking room around them, how the
                cover lift swings or slides, and whether the service panel still opens freely once
                the steps are in place.
              </p>
              <p className="mt-3 text-body text-ink/80 sm:text-body-lg" data-id="element-2187" data-mp="1kw9a8uquyj1s">
                Nearby doors and gates matter too, along with Michigan winter conditions on the
                surface where people step.
              </p>
            </div>

            <div className="lg:col-span-6 lg:pl-2" data-id="element-2188" data-mp="3uwo0zzfkxjgl">
              <ul className="space-y-2 border-t border-ink/15 pt-5" data-id="element-2189" data-mp="3uvcz655p2ls4">
                <li className="text-[16.5px] leading-6 text-ink/80" data-id="element-2190" data-mp="3gxemr5axozf9">
                  Walking room on the entry side
                </li>
                <li className="text-[16.5px] leading-6 text-ink/80" data-id="element-2191" data-mp="3luesff3vg05u">
                  Clearance for cover-lift movement
                </li>
                <li className="text-[16.5px] leading-6 text-ink/80" data-id="element-2192" data-mp="3bxsaoz6udp81">Access to the service panel</li>
                <li className="text-[16.5px] leading-6 text-ink/80" data-id="element-2193" data-mp="2y6dlgamccrsj">
                  Nearby doors, gates and walkways
                </li>
                <li className="text-[16.5px] leading-6 text-ink/80" data-id="element-2194" data-mp="3369tcky5t3dd">
                  Winter conditions on the surrounding surface
                </li>
              </ul>
              <p className="mt-5 text-[16px] leading-6 text-ink/70" data-id="element-2195" data-mp="2t7u12enebo2a">
                Check dimensions before purchase, and keep walking surfaces clear and maintained
                according to the product's guidance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Supporting manufacturer references stay available on demand. */}
      <section aria-label="Manufacturer information" className="w-full bg-white" data-id="element-2196" data-mp="2jhc1noyuzs5x">
        <div className="mx-auto max-w-container px-5 py-6 sm:px-8" data-id="element-2197" data-mp="2emokkb66q01d">
          <Disclosure label="Manufacturer Information" data-id="element-2198" data-mp="2sj22flems3gd">
            <p className="max-w-3xl text-[16px] leading-6 text-ink/75" data-id="element-2199" data-mp="2oiro9lgoq6qf">
              Browse the specifications, then ask us about fit and availability.
            </p>
            <ul className="mt-3 divide-y divide-ink/10" data-id="element-2200" data-mp="329endeqcxyn1">
              {manufacturers.map(item => <li key={item.label} data-id="element-2201" data-mp="2xd6903jzi9gx">
                  <a href={item.href} target="_blank" rel="noopener noreferrer" className="group flex min-h-[44px] items-center justify-between gap-4 py-3 text-[16px] font-medium text-ink transition-colors duration-150 ease-out hover:text-pegasus-blue" data-id="element-2202" data-mp="3c7udbednf1uy">
                    {item.label}
                    <ExternalLinkIcon className="h-4 w-4 shrink-0 text-pegasus-blue group-hover:text-pegasus-magenta" aria-hidden="true" data-id="element-2203" data-mp="3oax043hf3bje" />
                  </a>
                </li>)}
            </ul>
          </Disclosure>
        </div>
      </section>

      {/* Showroom split */}
      <section aria-labelledby="measure-heading" className="w-full border-y border-ink/10 bg-white" data-id="element-2204" data-mp="5uuhxvabrmfk">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-2205" data-mp="atxqvqtfecst">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-12" data-id="element-2206" data-mp="fu24n92y8tfc">
            <div className="lg:col-span-6" data-id="element-2207" data-mp="2coomgqup35gh">
              <h2 id="measure-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-2208" data-mp="2hlyfshl7g19l">
                Bring Photos. We&rsquo;ll Help With the Rest.
              </h2>
              <p className="mt-4 text-body text-ink/80 sm:text-body-lg" data-id="element-2209" data-mp="25b7682ii260m">
                The fastest way to narrow the options is to bring a few photos of the entry side and
                the space you have to work with. If you know the spa make, model or cabinet height,
                bring those details too; if not, we can help you figure out what matters.
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-2210" data-mp="2o271flgl8c2c">
                <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn bg-pegasus-blue px-5 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-ink" data-id="element-2211" data-mp="2j33ufvh1rvrq">
                  <ClockIcon className="h-4 w-4" aria-hidden="true" data-id="element-2212" data-mp="12p3y0uheuo98" />
                  Location &amp; Hours
                </SiteLink>
                <a href={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 rounded-btn border border-ink/25 px-5 text-[16px] font-medium text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-2213" data-mp="2ri9x8e2xu14k">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-2214" data-mp="2mjf7es6jzcay" />
                  Call or Text {business.phone}
                </a>
              </div>
            </div>

            <div className="lg:col-span-6" data-id="element-2215" data-mp="3f8m2tiapnlrz">
              <div className="aspect-[4/3] w-full overflow-hidden rounded-card" data-id="element-2216" data-mp="3f7ktbi9cf7cd">
                <img src={images.pathHotTubs} alt="Hot tub set into a residential wood deck surrounded by trees" className="h-full w-full object-cover object-center" loading="lazy" decoding="async" data-id="element-2217" data-mp="30alaszcydu34" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navy CTA */}
      <section aria-labelledby="fit-cta-heading" className="w-full bg-ink" data-id="element-2218" data-mp="bwmp192dmgg2">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-2219" data-mp="6zcu5dw7xcku">
          <div className="max-w-3xl" data-id="element-2220" data-mp="kymacdkfc6ot">
            <h2 id="fit-cta-heading" className="text-h2-mobile font-semibold text-white lg:text-h2-desktop" data-id="element-2221" data-mp="kxks9p4zqjxl">
              Find Steps That Fit the Spa and the Space
            </h2>
            <p className="mt-4 text-body text-white/85 sm:text-body-lg" data-id="element-2222" data-mp="3pd2hw9gd8yuq">
              Send a few photos or stop in, and we can help you sort through what
              works with your spa and your setup.
            </p>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-2223" data-mp="3teo1serbidef">
            <SiteLink to={routes.contact} className="inline-flex min-h-[48px] items-center rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-2224" data-mp="3td4h8bympg6u">
              Contact Us
            </SiteLink>
            <SiteLink to={routes.specials} className="inline-flex min-h-[48px] items-center rounded-btn border-2 border-white/70 px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-white hover:text-ink" data-id="element-2225" data-mp="14krj8g201ynx">
              See Current Specials
            </SiteLink>
          </div>
        </div>
      </section>

      {/* Related links */}
      <section aria-label="Related pages" className="w-full border-t border-ink/10 bg-white" data-id="element-2226" data-mp="qmujxbw5ve5k">
        <div className="mx-auto max-w-container px-5 py-6 sm:px-8" data-id="element-2227" data-mp="qonqysaos0e3">
          <ul className="flex flex-col gap-x-8 gap-y-1 sm:flex-row sm:flex-wrap sm:items-center" data-id="element-2228" data-mp="vkcm8jfoav4l">
            {relatedLinks.map(link => <li key={link.label} data-id="element-2229" data-mp="271cmc189ie96">
                <SiteLink to={link.href} className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-2230" data-mp="2701ade4ny06z">
                  {link.label}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2231" data-mp="3mrmzpkr0ah6d" />
                </SiteLink>
              </li>)}
          </ul>
        </div>
      </section>
    </main>;
}